import { useMenu } from '@restaurant/api-client/react';
import { Images, ImagePlus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { Field, Input } from '@/components/ui/input';
import { Spinner, Tooltip } from '@/components/ui/misc';
import { api } from '@/lib/api';
import { toastError } from '@/lib/errors';
import { cn } from '@/lib/utils';

const MAX = 5 * 1024 * 1024;
const OK_TYPES = ['image/jpeg', 'image/png'];
const isJpegOrPng = (url: string) => /\.(jpe?g|png)(\?.*)?$/i.test(url);

/** Send a picture: a file from this device or a menu photo, with an optional caption. */
export function ImageSendDialog({ conversationId, caption: initialCaption, onSent }: { conversationId: string; caption: string; onSent: () => void }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [menuUrl, setMenuUrl] = useState<string | null>(null);
  const [caption, setCaption] = useState('');
  const [busy, setBusy] = useState(false);
  const { data: menu } = useMenu();
  const photos = useMemo(
    () => (menu ?? []).flatMap((c) => c.items).filter((i) => i.imageUrl && isJpegOrPng(i.imageUrl)).map((i) => ({ id: i.id, name: i.name, url: i.imageUrl! })),
    [menu],
  );
  const preview = useMemo(() => (file ? URL.createObjectURL(file) : menuUrl), [file, menuUrl]);
  useEffect(() => () => void (file && preview && URL.revokeObjectURL(preview)), [file, preview]);
  useEffect(() => {
    if (open) setCaption(initialCaption);
    else {
      setFile(null);
      setMenuUrl(null);
    }
  }, [open, initialCaption]);

  const pick = (f: File | undefined) => {
    if (!f) return;
    if (!OK_TYPES.includes(f.type)) return toast.error('WhatsApp only accepts JPEG or PNG pictures');
    if (f.size > MAX) return toast.error('Pictures must be 5 MB or smaller');
    setMenuUrl(null);
    setFile(f);
  };

  const send = async () => {
    setBusy(true);
    try {
      await api.conversations.sendImage(conversationId, file ? { file, filename: file.name, caption } : { imageUrl: menuUrl!, caption });
      setOpen(false);
      onSent();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Tooltip content="Send a menu photo">
        <DialogTrigger asChild>
          <Button variant="outline" size="icon" aria-label="Send a menu photo"><Images /></Button>
        </DialogTrigger>
      </Tooltip>
      <DialogContent title="Send a picture" description="JPEG or PNG, up to 5 MB. Needs the 24-hour window to be open.">
        <div className="grid gap-3">
          <label
            className="flex cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border border-dashed p-4 text-sm text-muted-foreground hover:bg-accent"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              pick(e.dataTransfer.files[0]);
            }}
          >
            {preview ? <img src={preview} alt="" className="max-h-56 rounded-md object-contain" /> : <ImagePlus className="size-6" />}
            <span>{file ? file.name : 'Choose or drop a picture'}</span>
            <input type="file" accept="image/jpeg,image/png" className="hidden" onChange={(e) => pick(e.target.files?.[0])} />
          </label>
          {photos.length ? (
            <div>
              <p className="mb-1 text-xs text-muted-foreground">Or a menu photo</p>
              <div className="flex max-h-32 flex-wrap gap-1.5 overflow-y-auto">
                {photos.map((p) => (
                  <button
                    key={p.id}
                    title={p.name}
                    onClick={() => {
                      setFile(null);
                      setMenuUrl(p.url);
                      if (!caption) setCaption(p.name);
                    }}
                    className={cn('size-14 overflow-hidden rounded-md border-2', menuUrl === p.url ? 'border-primary' : 'border-transparent')}
                  >
                    <img src={p.url} alt={p.name} className="size-full object-cover" />
                  </button>
                ))}
              </div>
            </div>
          ) : null}
          <Field label="Caption (optional)"><Input value={caption} maxLength={1024} onChange={(e) => setCaption(e.target.value)} /></Field>
          <Button onClick={() => void send()} disabled={busy || (!file && !menuUrl)}>{busy ? <Spinner /> : null} Send picture</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
